import React, { useEffect, useState } from "react";
import { Field, FieldInputProps, FormikProps, useFormikContext } from "formik";
import { Input, Select, Form as SForm, Grid, FormField as SFormField, FormInput as SFormInput, FormSelect as SFormSelect, GridColumn } from "@/components/bp/bp-ui";
import { useLengthConversion, HeightUnit } from "../../Hooks/UseLengthConversion";

type Option = { key: string; value: string; text: string };

interface Props {
    // canonical meters field name (e.g. fn("heightInM"))
    nameM: string;
    // optional unit field name to persist user's selection (e.g. fn("heightUnit"))
    nameUnit?: string;
    label?: string;
    initialUnit?: HeightUnit;
    selectOptions?: Option[];
    // storage precision (meters) e.g., 3 => 0.001 m
    storagePrecisionM?: number;
    // how many decimal places to allow for inches (0 => integer inches; 1 => 0.1 in, etc.)
    inchesPrecision?: number;

    disabled?: boolean;
}

/**
 * HeightWithUnitField: Formik-aware component that keeps canonical meters in Formik
 * and lets the user edit either meters or feet/inches.
 *
 * Behavior:
 * - Initializes hook from formik.values[nameM]
 * - User edits inputs (local display); on blur the hook setters are called then
 *   the component syncs hook.meters -> formik.setFieldValue(nameM, meters)
 * - If nameUnit is supplied, the chosen unit is persisted to formik as well.
 */
export default function HeightWithUnitField(props: Props) {
    const {
        nameM,
        nameUnit,
        label = "Height",
        initialUnit = "m",
        selectOptions = [
            { key: "m", value: "m", text: "Meters (m)" },
            { key: "ft", value: "ft", text: "Feet / Inches (ft/in)" },
        ],
        storagePrecisionM = 3,
        inchesPrecision = 2,
        disabled = false,
    } = props;

    const formik = useFormikContext<any>() as FormikProps<any>;
    const formUnitValue = nameUnit ? (formik.values?.[nameUnit] as HeightUnit | undefined) : undefined;

    // Initialize stateful hook with current form value
    const initialMeters = formik.values?.[nameM] ?? null;
    const length = useLengthConversion(initialMeters, { inchesPrecision, storagePrecisionM });

    // Unit state: either persisted in form or local
    const [unit, setUnit] = useState<HeightUnit>((formUnitValue as HeightUnit) ?? initialUnit);

    // local display values to avoid janky typing
    const [metersDisplay, setMetersDisplay] = useState<string>(() => length.meters == null ? "" : String(length.meters));
    const [feetDisplay, setFeetDisplay] = useState<string>(() => length.feet == null ? "" : String(length.feet));
    const [inchesDisplay, setInchesDisplay] = useState<string>(() => length.inches == null ? "" : String(length.inches));

    const inchesFactor = Math.pow(10, inchesPrecision);
    const inchesStep = 1 / inchesFactor;

    // Keep hook in sync when Formik changes the canonical meters externally
    useEffect(() => {
        const formMeters = formik.values?.[nameM] ?? null;
        // Avoid unnecessary updates if same rounded value
        if (formMeters === null && length.meters === null) return;
        if (formMeters !== null && length.meters !== null && Number(formMeters) === Number(length.meters)) return;
        length.setMeters(formMeters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values?.[nameM]]);

    // When hook's canonical meters changes, persist to Formik and update displays
    useEffect(() => {
        // push hook meters to formik (can be null)
        if (formik.values?.[nameM] !== length.meters) {
            formik.setFieldValue(nameM, length.meters);
        }
        // update displays
        if (length.meters == null) {
            setMetersDisplay("");
            setFeetDisplay("");
            setInchesDisplay("");
        } else {
            setMetersDisplay(String(length.meters));
            setFeetDisplay(length.feet == null ? "" : String(length.feet));
            // format inches according to precision
            setInchesDisplay(length.inches == null ? "" : (inchesPrecision === 0 ? String(Math.round(length.inches)) : length.inches.toFixed(inchesPrecision)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [length.meters, length.feet, length.inches]);

    // when form unit field changes externally, sync
    useEffect(() => {
        if (formUnitValue && formUnitValue !== unit) setUnit(formUnitValue);
    }, [formUnitValue]);

    // persist unit into formik when changed locally
    useEffect(() => {
        if (!nameUnit) return;
        if (formik.values?.[nameUnit] !== unit) {
            formik.setFieldValue(nameUnit, unit);
        }
    }, [unit]);

    // Handlers
    const onUnitChange = (_e: any, data: any) => {
        const newUnit = data.value as HeightUnit;
        setUnit(newUnit);
    };

    // meters handlers (keep local until blur)
    const onMetersChange = (_e: any, data: any) => setMetersDisplay(data.value);
    const onMetersBlur = () => {
        const raw = (metersDisplay ?? "").toString().trim();
        if (raw === "") {
            length.setMeters(null);
            return;
        }
        const parsed = Number(raw);
        if (Number.isNaN(parsed)) {
            length.setMeters(null);
            return;
        }
        length.setMeters(parsed);
    };

    // feet/inches handlers (keep local until blur)
    const onFeetChange = (_e: any, data: any) => setFeetDisplay(data.value);
    const onInchesChange = (_e: any, data: any) => setInchesDisplay(data.value);
    const onFeetInchesBlur = () => {
        const feetRaw = (feetDisplay ?? "").toString().trim();
        const inchesRaw = (inchesDisplay ?? "").toString().trim();
        if (feetRaw === "" && inchesRaw === "") {
            length.setMeters(null);
            return;
        }
        const feetNum = feetRaw === "" ? 0 : Number(feetRaw);
        const inchesNum = inchesRaw === "" ? 0 : Number(inchesRaw);
        if (Number.isNaN(feetNum) || Number.isNaN(inchesNum)) {
            length.setMeters(null);
            return;
        }
        // set using hook which rounds according to inchesPrecision & storagePrecisionM
        length.setFeetInches(feetNum, inchesNum);
    };

    return (
        <>
            <Field name={nameUnit}>
                {({ field }: { field: FieldInputProps<any> }) => (
                    <SFormField disabled={disabled}>
                        <label>Unit</label>
                        <SFormSelect
                            options={selectOptions}
                            value={unit}
                            onChange={onUnitChange}
                            placeholder="Unit"
                            disabled={disabled}
                        />
                    </SFormField>
                )}
            </Field>

            {unit === "m" ? (
                <SFormInput label={label}>
                    <Input
                        value={metersDisplay}
                        onChange={onMetersChange}
                        onBlur={onMetersBlur}
                        type="number"
                        step={0.01}
                        style={{ minWidth: 0 }}
                        action={{ content: "m", disabled: true }}
                        disabled={disabled}
                    />
                </SFormInput>
            ) : (
                // Feet + Inches inputs in a compact grid
                <SFormField>
                    <label>{label} (ft / in)</label>
                    <Grid columns={2}>
                        <GridColumn>
                            <Input
                                fluid
                                label="ft"
                                value={feetDisplay}
                                onChange={onFeetChange}
                                onBlur={onFeetInchesBlur}
                                type="number"
                                step={1}
                                min={0}
                                disabled={disabled}
                            />
                        </GridColumn>
                        <GridColumn>
                            <Input
                                fluid
                                label="in"
                                value={inchesDisplay}
                                onChange={onInchesChange}
                                onBlur={onFeetInchesBlur}
                                type="number"
                                step={inchesStep}
                                min={0}
                                disabled={disabled}
                            // max is not enforced rigidly because we allow normalization on blur
                            />
                        </GridColumn>
                    </Grid>
                </SFormField>
            )}
        </>
    );
}